import { Star, ExternalLink, Quote } from "lucide-react";

const platforms = [
  {
    name: "Google",
    rating: 4.7,
    count: 114,
    url: "https://www.google.com/maps/place/GARAGE+DESCHAMPS+AUTO/data=!4m2!3m1!1s0x0:0xc8da19de94393b48",
    accent: "from-[#4285F4] via-[#34A853] to-[#FBBC05]",
    badge: "bg-white border border-slate-200",
    logo: (
      <svg viewBox="0 0 48 48" className="h-8 w-8" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.1 29.3 4 24 4c-7.7 0-14.4 4.4-17.7 10.7z"/>
        <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3c-2 1.4-4.5 2.2-7.3 2.2-5.3 0-9.7-3.3-11.3-8L6.1 32C9.3 39.2 16 44 24 44z"/>
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.5l6.3 5.3C41.1 36 44 30.5 44 24c0-1.3-.1-2.3-.4-3.5z"/>
      </svg>
    ),
  },
  {
    name: "Vroomly",
    rating: 4.8,
    count: 70,
    url: "https://www.vroomly.com/garages/garage-deschamps-auto-74930-france/",
    accent: "from-pink-500 via-fuchsia-500 to-purple-500",
    badge: "bg-gradient-to-br from-pink-50 to-purple-50 border border-pink-100",
    logo: (
      <div className="h-8 w-8 grid place-items-center rounded-md bg-gradient-to-br from-pink-500 to-fuchsia-600 text-white font-display font-black text-base">
        V
      </div>
    ),
  },
];

const testimonials = [
  {
    quote:
      "Très bonne intervention. Accueil très sympa, bons mécaniciens qui conseillent. Prix très raisonnables. Garage à recommander 👍",
    source: "Vroomly",
    rating: 5,
  },
  {
    quote:
      "J'ai pu laisser le véhicule la veille dans l'heure de midi ce qui est très appréciable. Changement de courroie et révision faits dans le délai annoncé. Personnel très agréable et donne leurs conseils et avis sur l'état du véhicule. Je recommande. Merci à vous !",
    source: "Vroomly",
    rating: 5,
  },
];

function Stars({ value, size = 16 }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${value} sur 5`}>
      {[1, 2, 3, 4, 5].map((i) => {
        const filled = i <= Math.floor(value);
        const half = !filled && i - value < 1 && i - value > 0;
        return (
          <span key={i} className="relative inline-block" style={{ width: size, height: size }}>
            <Star size={size} className="text-slate-200 fill-slate-200" />
            {(filled || half) && (
              <span
                className="absolute inset-0 overflow-hidden"
                style={{ width: filled ? "100%" : `${(value - (i - 1)) * 100}%` }}
              >
                <Star size={size} className="text-amber-400 fill-amber-400" />
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}

export default function Reviews() {
  const totalReviews = platforms.reduce((s, p) => s + p.count, 0);
  const avg =
    platforms.reduce((s, p) => s + p.rating * p.count, 0) / totalReviews;

  return (
    <section
      id="avis"
      data-testid="reviews-section"
      className="py-24 lg:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
              Avis clients
            </div>
            <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter text-slate-900">
              Ils nous font <br />
              <span className="text-slate-500">confiance.</span>
            </h2>
          </div>

          <div className="flex items-center gap-5 bg-slate-50 border border-slate-200 rounded-lg px-6 py-4">
            <div className="text-right">
              <div className="font-display font-black text-3xl md:text-4xl text-slate-900 leading-none">
                {avg.toFixed(1)}
                <span className="text-slate-400 text-2xl">/5</span>
              </div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">
                Note globale
              </div>
            </div>
            <div className="h-12 w-px bg-slate-200" />
            <div>
              <Stars value={avg} size={20} />
              <div className="text-xs text-slate-500 mt-1.5">
                {totalReviews} avis vérifiés
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {platforms.map((p) => (
            <a
              key={p.name}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`review-platform-${p.name.toLowerCase()}`}
              className="group relative overflow-hidden bg-slate-50 border border-slate-200 rounded-lg p-7 lift hover:bg-white hover:border-blue-300 hover:shadow-xl"
            >
              <div
                className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${p.accent}`}
              />
              <div className="flex items-start justify-between gap-4 mb-5">
                <div className="flex items-center gap-3">
                  <div className={`h-12 w-12 grid place-items-center rounded-md ${p.badge}`}>
                    {p.logo}
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                      Avis
                    </div>
                    <div className="font-display font-black text-xl text-slate-900">
                      {p.name}
                    </div>
                  </div>
                </div>
                <ExternalLink
                  size={18}
                  className="text-slate-400 group-hover:text-blue-600 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                />
              </div>

              <div className="flex items-end gap-3 mb-2">
                <div className="font-display font-black text-5xl text-slate-900 leading-none">
                  {p.rating.toFixed(1)}
                </div>
                <div className="text-slate-400 font-display font-bold text-xl pb-1">
                  /5
                </div>
              </div>
              <Stars value={p.rating} size={18} />
              <div className="mt-4 text-sm text-slate-600">
                Basé sur{" "}
                <span className="font-semibold text-slate-900">
                  {p.count} avis
                </span>{" "}
                clients
              </div>

              <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 group-hover:gap-2.5 transition-all">
                Voir tous les avis
                <ExternalLink size={14} />
              </div>
            </a>
          ))}
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-5">
          {testimonials.map((t, i) => (
            <figure
              key={t.quote}
              data-testid={`testimonial-${i}`}
              className="relative bg-slate-950 text-white rounded-lg p-7 overflow-hidden"
            >
              <Quote
                size={64}
                className="absolute -top-2 -right-2 text-white/5"
                strokeWidth={1}
              />
              <div className="relative">
                <Stars value={t.rating} size={16} />
                <blockquote className="mt-4 text-[15px] leading-relaxed text-slate-200 italic">
                  « {t.quote} »
                </blockquote>
                <figcaption className="mt-5 text-xs font-bold uppercase tracking-[0.2em] text-blue-400">
                  Avis vérifié · {t.source}
                </figcaption>
              </div>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
